import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useAppDispatch } from "hooks/redux";
import { roundNumber } from "helpers/Helpers";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import SelectNew from "components/select/SelectNew";
import Checkbox from "components/form/checkbox/Checkbox";
import Tabs from "components/tabs/Tabs";

import "pages/secure/facility/product/Variant.scss";

import { useTranslation } from "react-i18next";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { PostVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { TTaxLines } from "redux/reducers/models/facility";
import { showError } from "redux/actions/ui";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";

interface IVariantState {
  id: number;
  product_id: number;
  product_options: any;
  title: string;
  option1: string;
  option2: string;
  option3: string;
  price: number;
  original_price: number;
  cost: number;
  taxable: boolean;
  barcode: string;
  sku: string;
  tracked: boolean;
  product: any;
  editing: boolean;
}

export default function VariantNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const { productId }: any = useParams();
  const dispatch = useAppDispatch();
  const [variantState, setVariantState] = useState<IVariantState>({
    id: null,
    product_id: null,
    product_options: null,
    title: "",
    option1: "",
    option2: "",
    option3: "",
    price: null,
    original_price: null,
    cost: null,
    taxable: false,
    barcode: "",
    sku: "",
    tracked: false,
    product: null,
    editing: false,
  });
  const [selected, setSelected] = useState(0);
  const [markup, setMarkup] = useState<number>(0);
  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [hidePriceAfterTax, setHidePriceAfterTax] = useState<boolean>(false);
  const [taxLines, setTaxLines] = useState<TTaxLines[]>(undefined);

  const tabs = [
    {
      id: "overview",
      content: t("Overview"),
    },
    {
      id: "pricing",
      content: t("Pricing & Taxes"),
    },
    {
      id: "Logistics",
      content: t("Logistics"),
    },
  ];

  const [productOptions] = useState({
    taxlines: [
      {
        id: 0,
        name: "HST",
        tax: 0.13,
      },
      {
        id: 1,
        name: "GST",
        tax: 0.05,
      },
      {
        id: 2,
        name: "PST",
        tax: 0.07,
      },
    ] as TTaxLines[],
  });

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    console.log(event);
    console.log(id, value);
    setVariantState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCheckboxChange(event: any) {
    const { id, checked } = event.target;
    setVariantState(prevState => ({ ...prevState, [id]: checked }));
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  const toggleTaxLines = (value: number) => {
    console.log("valuetax", value);
    setSelectedTaxLine(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  function getPriceAfterTax() {
    const originalPrice = variantState?.price ?? 0;
    let price = variantState?.price ?? 0;

    for (let i = 0; i < selectedTaxLine.length; i++) {
      price += originalPrice * taxLines.find(tax => tax.id === selectedTaxLine[i])?.tax;
    }

    return roundNumber(price, 2);
  }

  function getTaxNames() {
    if (!taxLines) {
      return;
    }
    const taxNames = [];
    for (let i = 0; i < selectedTaxLine.length; i++) {
      taxNames[i] = taxLines.find(line => line.id === selectedTaxLine[i]).name;
    }
    return taxNames.join(", ");
  }

  useEffect(() => {
    void loadProduct();
    void loadTaxLines();
  }, []);

  async function loadProduct() {
    const productRes = await GetProduct({ id: productId, extended: true }, true);
    if (productRes.status !== StatusCode.OK) {
      return;
    }

    const product = productRes.data[0];
    console.log("Product", product);

    setVariantState(prev => ({
      ...prev,
      product_id: product.id,
      product: product,
    }));
  }

  async function createVariant() {
    if (!variantState.title) {
      dispatch(showError("Title is required"));
      return;
    }

    if (!variantState.cost || !variantState.price) {
      dispatch(showError("Missing Pricing Information"));
      return;
    }

    if (variantState.price < variantState.cost) {
      dispatch(showError("Retail Price must be greater than Cost"));
      return;
    }

    if (variantState.taxable && selectedTaxLine.length === 0) {
      dispatch(showError("Taxable variant must have at least one tax line"));
      return;
    }

    if (variantState.product?.options[0] && !variantState.option1) {
      dispatch(showError(`${variantState.product?.options[0].name} is required`));
      return;
    }

    if (variantState.product?.options[1] && !variantState.option2) {
      dispatch(showError(`${variantState.product?.options[1].name} is required`));
      return;
    }

    if (variantState.product?.options[2] && !variantState.option3) {
      dispatch(showError(`${variantState.product?.options[2].name} is required`));
      return;
    }

    const variantRes = await PostVariant(variantState, true);

    if (variantRes.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/product/" + String(productId));
    return;
  }

  async function loadTaxLines() {
    const unitRes = await GetTaxLine(null, false);

    if (unitRes.status !== StatusCode.OK) {
      return;
    }

    setTaxLines(unitRes.data);
  }

  function handleMarkupChange(value: number) {
    if (!variantState?.cost) {
      showError("Cost is required");
    } else {
      setMarkup(value);
      setVariantState(prev => ({
        ...prev,
        price: Number(roundNumber(variantState.cost * (value / 100) + variantState.cost, 2)),
      }));
    }
  }

  function handleCostChange(value: number) {
    if (value === 0) {
      setMarkup(0);
      setVariantState(prev => ({
        ...prev,
        cost: Number(roundNumber(value, 2)),
      }));
      return;
    }
    const price = Number(variantState?.price ?? 0);
    setMarkup(((price - value) / value) * 100);
    setVariantState(prev => ({
      ...prev,
      cost: Number(roundNumber(value, 2)),
    }));
  }

  function handlePriceChange(value: number) {
    const cost = Number(variantState?.cost ?? 0);
    setMarkup(((value - cost) / cost) * 100);
    setVariantState(prev => ({
      ...prev,
      price: Number(roundNumber(value, 2)),
    }));
  }

  const primaryAction = {
    content: t("secure.facility.product.variant_new.001"),
    action: createVariant,
  };

  const secondaryActions = [
    {
      content: "Cancel",
      action: () => history.push("/admin/product/" + String(productId)),
    },
  ];

  return (
    <Page
      title={t("secure.facility.product.variant_new.002")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <Page.Section>
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
      </Page.Section>
      <br />
      {selected === 0 ? (
        <Card title={t("secure.facility.product.variant_new.003")}>
          <Card.Section>
            <Input
              value={variantState.title || ""}
              label="Title"
              id="title"
              placeholder="Title"
              onChange={handleInputChange}
            />
          </Card.Section>
          <Card.Section>
            {variantState.product?.options?.map((option: any, index: number) => {
              return (
                <Input
                  //defaultValue={variantState.optionList[index].value}
                  key={index}
                  type="text"
                  id={`option${index + 1}`}
                  onChange={handleInputChange}
                  label={option.name}
                />
              );
            })}
          </Card.Section>
        </Card>
      ) : null}
      {selected === 1 ? (
        <Card
          title={t("Pricing")}
          titleActions={[
            {
              content: (
                <Checkbox
                  id="taxable"
                  size="medium"
                  value={variantState.taxable}
                  checked={variantState.taxable}
                  onChange={handleCheckboxChange}
                  label={t("Charge Taxes")}
                />
              ),
              action: () => {},
            },
          ]}
        >
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  id="cost"
                  value={variantState.cost || ""}
                  type="number"
                  prefix="$"
                  label={t("secure.facility.product.product_new.015")}
                  onChange={e => handleCostChange(Number(e.target.value))}
                  placeholder="0.00"
                  //error={!validated("cost") && attempted}
                />
                <Input
                  id="markup"
                  value={Number(roundNumber(markup, 2)) || ""}
                  onChange={e => handleMarkupChange(Number(e.target.value))}
                  type="number"
                  prefix="%"
                  label={t("Markup")}
                  placeholder="0.00"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  id="retail_price"
                  value={variantState.price || ""}
                  type="number"
                  prefix="$"
                  label={t("Retail Price")}
                  onChange={e => handlePriceChange(Number(e.target.value))}
                  placeholder="0.00"
                  //error={!validated("price") && attempted}
                />
                <div />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section
            title={t("Price after tax")}
            sectionTitleActions={[
              {
                content: <FontAwesomeIcon icon={hidePriceAfterTax ? faChevronUp : faChevronDown} />,
                action: () => {
                  setHidePriceAfterTax(!hidePriceAfterTax);
                },
              },
            ]}
          >
            {!hidePriceAfterTax ? (
              <FormLayout>
                <FormLayout.Group>
                  <SelectNew
                    id="tax-lines-select"
                    label={t("Tax lines")}
                    placeholder={getTaxNames() ?? "Select tax lines"}
                    multiValue={selectedTaxLine}
                    options={taxLines?.map(taxLine => ({
                      id: taxLine.id,
                      label: taxLine.name,
                    }))}
                    onChange={value => toggleTaxLines(value)}
                  />
                  <Input
                    id="price_after_tax"
                    value={getPriceAfterTax() || ""}
                    type={"number"}
                    prefix="$"
                    label={t("Price after tax")}
                    placeholder="0.00"
                    //error={!validated("price") && attempted}
                    readOnly={true}
                  />
                </FormLayout.Group>
              </FormLayout>
            ) : null}
          </Card.Section>
        </Card>
      ) : null}

      {selected === 2 ? (
        <Card
          title={t("secure.facility.product.variant_new.010")}
          subtitle="Manage your inventory here."
          titleActions={[
            {
              content: (
                <Checkbox
                  id="tracked"
                  size="medium"
                  value={variantState.tracked}
                  checked={variantState.tracked}
                  onChange={handleCheckboxChange}
                  label={t("secure.facility.product.variant_new.013")}
                />
              ),
              action: () => {},
            },
          ]}
        >
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={variantState.barcode || ""}
                  label={t("secure.facility.product.variant_new.011")}
                  id="barcode"
                  onChange={handleInputChange}
                />
                <Input
                  value={variantState.sku || ""}
                  label={t("secure.facility.product.variant_new.012")}
                  id="sku"
                  onChange={handleInputChange}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      ) : null}
    </Page>
  );
}
